import type { CustomFieldValues } from '@taki/data-model';
import type { StatusBadgeTone } from '@taki/ui';

/**
 * Fondation de donnees de l'app Cat Houses (les maisons ou vivent les chats).
 *
 * Regles de la cage :
 *  - acces donnees UNIQUEMENT via `@taki/sdk` (aucun import firebase/firestore) ;
 *  - aucune entite inventee : quatre FAMILLES du socle, specialisees par `type`
 *    et `customFields` (docs/PLAYBOOK-APP.md section 8).
 *
 * Les quatre familles et POURQUOI elles :
 *  - une maison  = `place`     de type 'cat-house'. La famille "lieu physique
 *    identifie" couvre explicitement le domicile. Elle n'a pas de champ `name`
 *    (elle identifie par type + adresse) : le nom d'usage de la maison vit donc
 *    dans `customFields.name`, l'adresse postale dans le champ natif `address`.
 *  - un chat     = `asset`     de type 'cat'. MEME vocabulaire que
 *    cat-manager-six et cat-logger-outside : les trois apps parlent des memes
 *    fiches. La famille n'a pas de lien vers un lieu, la maison du chat vit donc
 *    dans `customFields.houseId` (id d'un `place`) ; vide = chat sans maison.
 *  - une sortie  = `custody`   de type 'outside-visit', en LECTURE SEULE ici :
 *    c'est cat-logger-outside qui les ecrit. Un chat est en sortie tant qu'une
 *    custody le concernant n'a pas de `returnedAt`.
 *  - un ticket   = `ticket`    de type 'maintenance'. La famille "demande
 *    entrante prise en charge" : cette app EMET la demande, elle ne fait pas le
 *    travail. C'est la future app Maintenance qui prendra le relais, et le socle
 *    cable deja ce passage de main : `workOrder.ticketId` designe "le ticket a
 *    l'origine du travail". Cat Houses pose donc le ticket, Maintenance creera
 *    l'ordre de travail qui pointe dessus. Le lien inverse ne vit PAS ici (la
 *    famille ticket ne stocke aucun workOrderId) : c'est l'ordre de travail qui
 *    porte le lien, exactement comme le socle le prescrit.
 *    La famille `workOrder` a ete ecartee : le travail technique est le metier
 *    de Maintenance, pas celui de cette app. La famille ticket n'ayant pas de
 *    lien vers un lieu, la maison visee vit dans `customFields.houseId`.
 *
 * Ce fichier est le CONTRAT partage par l'ecran (memes noms d'exports et memes
 * cles customFields).
 */

// --- Types locaux declares dans taki.app.json ------------------------------

/** Type local de la famille `place` : une maison ou vivent des chats. */
export const HOUSE_TYPE = 'cat-house';

/** Type local de la famille `asset` : une fiche chat (partagee avec les apps soeurs). */
export const CAT_TYPE = 'cat';

/** Type local de la famille `custody` : une sortie de chat (ecrite par cat-logger-outside). */
export const OUTING_TYPE = 'outside-visit';

/**
 * Type local de la famille `ticket` : une demande de maintenance sur une maison.
 *
 * Volontairement nomme d'apres la NATURE de la demande, pas d'apres son
 * emetteur : la future app Maintenance ramassera tous les tickets 'maintenance'
 * de l'org, d'ou qu'ils viennent, sans avoir a connaitre Cat Houses.
 */
export const TASK_TYPE = 'maintenance';

// --- Statuts d'un ticket ---------------------------------------------------

/**
 * Les trois etats d'un ticket. Les VALEURS sont celles que le socle donne en
 * exemple pour `ticket.status` ('ouvert', 'en_cours', 'clos') ; les LIBELLES
 * sont ceux du fondateur (Ouvert, En cours, Ferme).
 *
 * Qui pose quoi : Cat Houses OUVRE la demande et peut la FERMER, rien d'autre.
 * 'en_cours' sera pose par la future app Maintenance quand elle prendra le
 * travail : cette app l'affiche et le filtre, elle ne l'ecrit jamais.
 */
export type TaskStatus = 'ouvert' | 'en_cours' | 'clos';

export interface TaskStatusOption {
  value: TaskStatus;
  label: string;
  /** La led de la pastille : vive quand il y a a faire, eteinte quand c'est fini. */
  tone: StatusBadgeTone;
}

export const TASK_STATUSES: TaskStatusOption[] = [
  { value: 'ouvert', label: 'Ouvert', tone: 'accent' },
  { value: 'en_cours', label: 'En cours', tone: 'success' },
  { value: 'clos', label: 'Ferme', tone: 'neutral' },
];

/** Statut d'un ticket qu'on vient d'ouvrir. */
export const DEFAULT_TASK_STATUS: TaskStatus = 'ouvert';

/** L'option d'affichage d'un statut ; repli sur "Ouvert" si la valeur est inconnue. */
export function taskStatusOptionOf(value: string): TaskStatusOption {
  return TASK_STATUSES.find((s) => s.value === value) ?? TASK_STATUSES[0]!;
}

/** Un ticket deja ferme n'a plus rien a subir depuis cette app. */
export function isTaskClosed(status: TaskStatus): boolean {
  return status === 'clos';
}

// --- Types vue (derives des familles socle) --------------------------------

export interface House {
  id: string;
  /** Nom d'usage, depuis `customFields.name` (la famille place n'a pas de `name`). */
  name: string;
  /** Champ natif `address` ; chaine vide si non renseignee. */
  address: string;
}

export interface Cat {
  id: string;
  name: string;
  /** Id du `place` ou vit le chat, depuis `customFields.houseId` ; '' = sans maison. */
  houseId: string;
}

export interface Task {
  id: string;
  /**
   * La maison visee, depuis `customFields.houseId` (id d'un `place`).
   * La famille ticket n'a pas de lien natif vers un lieu : elle vise un
   * demandeur (`contactId`), pas un endroit.
   */
  houseId: string;
  status: TaskStatus;
  /** Date prevue, date PURE 'YYYY-MM-DD' (cf. AGENTS.md section 7) ; '' si absente. */
  dueDate: string;
  /** La note technique, depuis `customFields.note`. */
  note: string;
}

// --- Helpers internes de lecture defensive ---------------------------------

/** Recupere le sac customFields d'un document quel qu'il soit, sans planter. */
function customFieldsOf(source: unknown): CustomFieldValues {
  if (source && typeof source === 'object' && 'customFields' in source) {
    const cf = (source as { customFields?: unknown }).customFields;
    if (cf && typeof cf === 'object' && !Array.isArray(cf)) {
      return cf as CustomFieldValues;
    }
  }
  return {};
}

/** Lit une propriete de premier niveau d'un objet inconnu. */
function fieldOf(source: unknown, key: string): unknown {
  if (source && typeof source === 'object' && key in source) {
    return (source as Record<string, unknown>)[key];
  }
  return undefined;
}

/** Lit une chaine de premier niveau, chaine vide par defaut. */
function stringOf(source: unknown, key: string): string {
  const raw = fieldOf(source, key);
  return typeof raw === 'string' ? raw : '';
}

/** Lit une chaine du sac customFields, chaine vide par defaut. */
function cfStringOf(cf: CustomFieldValues, key: string): string {
  const raw = cf[key];
  return typeof raw === 'string' ? raw : '';
}

/** Convertit une valeur Timestamp | Date | string | inconnu en Date ou null. */
function toDate(value: unknown): Date | null {
  if (value == null) {
    return null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'object' && typeof (value as { toDate?: unknown }).toDate === 'function') {
    try {
      const d = (value as { toDate: () => Date }).toDate();
      return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
    } catch {
      return null;
    }
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

// --- Helpers de mapping publics --------------------------------------------

/** Lit un place (type 'cat-house') vers la vue House. */
export function placeToHouse(place: unknown): House {
  const cf = customFieldsOf(place);
  return {
    id: stringOf(place, 'id'),
    name: cfStringOf(cf, 'name'),
    address: stringOf(place, 'address'),
  };
}

/** Lit un asset (type 'cat') vers la vue Cat. */
export function assetToCat(asset: unknown): Cat {
  const cf = customFieldsOf(asset);
  return {
    id: stringOf(asset, 'id'),
    name: stringOf(asset, 'name'),
    houseId: cfStringOf(cf, 'houseId'),
  };
}

/** Lit un ticket (type 'maintenance') vers la vue Task. */
export function ticketToTask(ticket: unknown): Task {
  const cf = customFieldsOf(ticket);
  const status = stringOf(ticket, 'status');
  return {
    id: stringOf(ticket, 'id'),
    houseId: cfStringOf(cf, 'houseId'),
    status: taskStatusOptionOf(status).value,
    dueDate: cfStringOf(cf, 'dueDate'),
    note: cfStringOf(cf, 'note'),
  };
}

/**
 * Les ids des chats actuellement dehors, lus depuis les custodies
 * 'outside-visit' : une sortie est en cours tant que `returnedAt` est vide.
 */
export function outsideCatIdsOf(custodies: unknown[]): Set<string> {
  const ids = new Set<string>();
  for (const custody of custodies) {
    if (!isType(custody, OUTING_TYPE) || !isActive(custody)) {
      continue;
    }
    if (toDate(fieldOf(custody, 'returnedAt')) != null) {
      continue;
    }
    const catId = stringOf(custody, 'itemAssetId');
    if (catId !== '') {
      ids.add(catId);
    }
  }
  return ids;
}

/**
 * Le patch `customFields` qui pose la maison d'un chat ; '' l'en retire.
 *
 * On ne poste QUE notre cle : depuis le correctif SDK (PR 32), `update` fusionne
 * `customFields` cle par cle en chemins pointes, donc les cles des apps soeurs
 * (caractere, photo, statut d'adoption) survivent sans qu'on ait a les reposter.
 * Reposter le sac entier serait meme risque : le SDK refuse les cles hors format
 * FieldPath, et une cle exotique posee par une autre app ferait lever notre
 * update alors qu'elle ne nous regarde pas.
 *
 * `null` (et pas '') pour retirer : c'est la semantique du socle, la cle est
 * SUPPRIMEE du document plutot que laissee vide (AGENTS.md section 7).
 */
export function catHousePatch(houseId: string): CustomFieldValues {
  return { houseId: houseId === '' ? null : houseId };
}

/** Le document est-il actif (non archive) ? Le soft delete se fait via archivedAt. */
export function isActive(doc: unknown): boolean {
  return toDate(fieldOf(doc, 'archivedAt')) == null;
}

/** Le document appartient-il au type local attendu ? */
export function isType(doc: unknown, type: string): boolean {
  return fieldOf(doc, 'type') === type;
}

// --- Helpers de date pour les formulaires ----------------------------------

/**
 * Date du jour au format 'YYYY-MM-DD', lue dans le fuseau LOCAL.
 * (`toISOString()` passerait par UTC et ferait glisser le jour en soiree.)
 */
export function todayIso(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

/**
 * Formate une date pure 'YYYY-MM-DD' en 'jj/mm/aaaa' ; tiret si absente.
 * Pas de `new Date()` ici : une date pure n'a pas d'heure, la reconvertir en
 * instant la ferait glisser d'un jour selon le fuseau (AGENTS.md section 7).
 */
export function formatDateIso(value: string): string {
  const parts = value.split('-');
  if (parts.length !== 3) {
    return '-';
  }
  const [year, month, day] = parts;
  return `${day}/${month}/${year}`;
}
